/*======================================================================================================================
  * Bluebell: src/app/state/mind-maps/mind-maps.effects
  * Last Edited: 11/08/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { HttpErrorResponse } from '@services/rest.service';
import { ApiService } from '@state/api.service';
import { deleteMindMapFailure } from '@state/mind-maps/mind-maps.actions';
import { MessageService } from 'primeng/api';
import * as mindMapsActions from '@state/mind-maps/mind-maps.actions';
import { MindMap } from '@state/mind-maps/mind-maps.interfaces';
import { ProjectActions } from '@state/projects/project.actions';
import { selectCurrentProjectId } from '@state/projects/project.selectors';
import { mergeMap, of, switchMap } from 'rxjs';
import { EffectHandlerService } from '@services/effect-handler.service';
import { map } from 'rxjs/operators';


@Injectable()
export class MindMapsEffects {
  private actions$ = inject(Actions);
  private api = inject(ApiService);
  private store = inject(Store);
  private msgService = inject(MessageService);
  private handler = inject(EffectHandlerService);


  createMindMap$ = createEffect(() => this.actions$.pipe(
    ofType(mindMapsActions.createMindMap),
    switchMap(({ mindMap }) => this.api.createMindMap(mindMap).pipe(
      this.handler.effectResponse({
        success: (response: MindMap) => mindMapsActions.createMindMapSuccess({ mindMap: response }),
        retry: mindMapsActions.createMindMap({ mindMap }),
        failure: mindMapsActions.createMindMapFailure(),
        failureMessage: 'Could not create mind map',
      })
    )
  )
  ));

  loadMindMaps$ = createEffect(() => this.actions$.pipe(
    ofType(mindMapsActions.loadMindMaps, ProjectActions.selectSuccess),
    concatLatestFrom(() => this.store.select(selectCurrentProjectId)),
    switchMap(([, project_uuid]) => {
      if (project_uuid) {
        return this.api.listMindMaps(project_uuid).pipe(
          this.handler.effectResponse({
            success: (mindMaps: MindMap[]) => mindMapsActions.loadMindMapsSuccess({ mindMaps }),
            retry: mindMapsActions.loadMindMaps(),
            failure: mindMapsActions.loadMindMapsFailure(),
            failureMessage: 'Could not retrieve mind maps',
          })
        );
      } else {
        return of(mindMapsActions.loadMindMapsSuccess({ mindMaps: []}));
      }
    })));

    updateMindMaps$ = createEffect(() => this.actions$.pipe(
      ofType(mindMapsActions.updateMindMap),
      switchMap(({ project, uuid }) =>
        this.api.mindMapTriggerUpdate(uuid, project).pipe(
          this.handler.effectResponse({
            success: (mindMap: MindMap) => mindMapsActions.updateMindMapSuccess({ mindMap }),
            retry: mindMapsActions.updateMindMap({ project, uuid }),
            failure: mindMapsActions.updateMindMapFailure(),
            failureMessage: 'Could not update mind map',
          })
        )))
    );

    deleteMindMap$ = createEffect(() => this.actions$.pipe(
      ofType(mindMapsActions.deleteMindMap),
      mergeMap(({ uuid }) => this.api.deleteMindMap(uuid).pipe(
        this.handler.effectResponse({
        success: () => mindMapsActions.deleteMindMapSuccess({ uuid }),
        retry: mindMapsActions.deleteMindMap({ uuid }),
        failure: ({ status, error }: HttpErrorResponse) => deleteMindMapFailure({ status, error }),
        })
      ))
    ));

    deleteMindMapFailure$ = createEffect(() => this.actions$.pipe(
      ofType(deleteMindMapFailure),
      map(({ error, status }) => {
        if (status === 403) {
          this.msgService.add({
            severity: 'error',
            summary: 'Could not delete mind map',
            detail: error,
            life: 8000
          });
        } else {
          console.error(error);
          this.msgService.add({
            severity: 'error',
            summary: 'Error deleting Mind Map',
            detail: 'Unexpected error occurred when deleting Mind Map',
            life: 8000
          });
        }
      })
    ), { dispatch: false });

    selectMindMap$ = createEffect(() => this.actions$.pipe(
      ofType(mindMapsActions.selectMindMap),
      switchMap(({ uuid }) => this.api.retrieveMindMap(uuid).pipe(
        this.handler.effectResponse({
          success: (mindMap: MindMap) => mindMapsActions.selectMindMapSuccess({ mindMap }),
          retry: mindMapsActions.selectMindMap({ uuid }),
          failure: mindMapsActions.selectMindMapFailure(),
          failureMessage: 'Failed to load mind map',
        })
      ))
    ));

    saveMindMap$ = createEffect(() => this.actions$.pipe(
      ofType(mindMapsActions.saveMindMap),
      switchMap(({ mindMap }) => this.api.updateMindMap(mindMap).pipe(
        this.handler.effectResponse({
          success: (_mindMap: MindMap) => mindMapsActions.saveMindMapSuccess({ mindMap: _mindMap }),
          retry: mindMapsActions.saveMindMap({ mindMap }),
          failure: mindMapsActions.saveMindMapFailure(),
          failureMessage: 'Could not save mind map',
        })
      ))
    ));
}
